import React, { useState } from "react";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 2 * 1024 * 1024;

function UploadSVG({ size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

export default function LogoUpload({
  userId,
  logoUrl,
  onUpload,
  onUploadingChange,
  size = 88,
  layout = "row",
  hint = null,
  showSuccessState = false,
}) {
  const { showToast } = useToast();
  const [uploading, setUploading] = useState(false);

  async function handleChange(e) {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      showToast("Only PNG, JPG or WebP images are accepted.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      showToast("Logo must be under 2 MB.", "error");
      e.target.value = "";
      return;
    }

    setUploading(true);
    onUploadingChange?.(true);
    try {
      const path = `${userId}/logo.png`;
      const { error: uploadErr } = await supabase.storage
        .from("profile-photos")
        .upload(path, file, { upsert: true, contentType: file.type || "image/png" });
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from("profile-photos")
        .getPublicUrl(path);
      const publicUrl = urlData.publicUrl;

      const { error: dbErr } = await supabase
        .from("profiles")
        .update({ logo_url: publicUrl })
        .eq("id", userId);
      if (dbErr) throw dbErr;

      onUpload(publicUrl);
      showToast("Logo uploaded and saved.", "success");
    } catch (err) {
      showToast(err.message || "Photo upload failed.", "error");
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
      e.target.value = "";
    }
  }

  const isColumn = layout === "column";

  const circle = logoUrl ? (
    <img
      src={logoUrl}
      alt="Business logo"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        border: "3px solid #FF8A00",
        boxShadow: isColumn
          ? "0 4px 18px rgba(255, 138, 0,0.25)"
          : "0 4px 14px rgba(255, 138, 0,0.25)",
        display: "block",
      }}
    />
  ) : (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: isColumn ? "rgba(255, 138, 0,0.06)" : "rgba(255, 138, 0,0.07)",
        border: isColumn
          ? "2px dashed rgba(255, 138, 0,0.4)"
          : "2px dashed rgba(255, 138, 0,0.35)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: isColumn ? "6px" : "4px",
        color: isColumn ? "rgba(255, 138, 0,0.8)" : "rgba(255, 138, 0,0.7)",
        fontSize: isColumn ? "0.75rem" : "0.65rem",
        fontWeight: isColumn ? 600 : undefined,
        textAlign: "center",
        lineHeight: 1.3,
      }}
    >
      <UploadSVG size={isColumn ? 24 : 20} />
      {isColumn ? "Tap to upload" : <><br />Upload<br />your logo</>}
    </div>
  );

  if (isColumn) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <input
          id="rqp-logo-upload"
          type="file"
          accept="image/png, image/jpeg, image/webp"
          onChange={handleChange}
          disabled={uploading}
          style={{ display: "none" }}
        />
        <label
          htmlFor="rqp-logo-upload"
          style={{ cursor: uploading ? "not-allowed" : "pointer" }}
        >
          {circle}
        </label>

        {showSuccessState && logoUrl ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#22C55E", fontWeight: 700, fontSize: "0.95rem" }}>
              Looks great! ✓
            </div>
            <label
              htmlFor="rqp-logo-upload"
              style={{
                color: "var(--muted, #6b7280)",
                fontSize: "0.82rem",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Change it →
            </label>
          </div>
        ) : uploading ? (
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--muted, #6b7280)" }}>
            Uploading…
          </p>
        ) : null}

        {hint && (
          <p className="muted small" style={{ margin: 0 }}>
            {hint}
          </p>
        )}
      </div>
    );
  }

  // layout === "row"
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "20px",
        flexWrap: "wrap",
      }}
    >
      <input
        id="rqp-logo-upload"
        type="file"
        accept="image/png, image/jpeg, image/webp"
        onChange={handleChange}
        disabled={uploading}
        style={{ display: "none" }}
      />
      <label
        htmlFor="rqp-logo-upload"
        style={{ cursor: uploading ? "not-allowed" : "pointer", flexShrink: 0 }}
        title={uploading ? "Uploading…" : "Click to upload logo"}
      >
        {circle}
      </label>

      <div>
        <label
          htmlFor="rqp-logo-upload"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            padding: "10px 18px",
            borderRadius: "12px",
            border: "1px solid rgba(140,100,80,0.22)",
            background: "rgba(255,255,255,0.76)",
            fontWeight: 700,
            fontSize: "0.875rem",
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
            transition: "opacity 0.2s",
            marginBottom: 0,
          }}
        >
          <UploadSVG size={15} />
          {uploading ? "Uploading…" : logoUrl ? "Replace logo" : "Upload logo"}
        </label>
        {hint && (
          <p className="muted small" style={{ margin: "7px 0 0" }}>
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}
